import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, VStack } from '@chakra-ui/react';
import { InputField } from '@themes/aegix';
import type { CredentialShape } from '../../../services';

// One discrete InputField per required key for the IdP API credentials.
// Parent still consumes `rawIdpCredentialsText` (JSON string) so the
// backend payload contract doesn't change; we serialize on every change.
//
// Bearer-token field has moved to the parent drawer (server-managed via
// auto-generate-on-create + BearerRotateModal).

export interface CredentialInputProps {
  hasCredentials: boolean;
  credentialShape: CredentialShape | null;
  rawIdpCredentialsText: string;
  onIdpCredentialsTextChange: (value: string) => void;
}

// Per-key field metadata. Keep in sync with IDP_CREDENTIAL_SHAPES in
// server/apps/scim/api/serializers.py and the per-IdP setup runbooks.
const FIELD_META: Record<
  string,
  { key: string; labelDefault: string; sensitive: boolean; placeholder?: string }
> = {
  api_token: {
    key: 'sso.apiToken',
    labelDefault: 'API token',
    sensitive: true,
    placeholder: 'Okta admin > Security > API > Tokens',
  },
  org_url: {
    key: 'sso.orgUrl',
    labelDefault: 'Org URL',
    sensitive: false,
    placeholder: 'https://your-org.okta.com',
  },
  integration_key: { key: 'sso.integrationKey', labelDefault: 'Integration key', sensitive: false },
  secret_key: { key: 'sso.secretKey', labelDefault: 'Secret key', sensitive: true },
  api_hostname: {
    key: 'sso.apiHostname',
    labelDefault: 'API hostname',
    sensitive: false,
    placeholder: 'api-xxxxxxxx.duosecurity.com',
  },
  tenant_id: {
    key: 'sso.tenantId',
    labelDefault: 'Tenant ID',
    sensitive: false,
    placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
  },
  client_id: { key: 'sso.clientId', labelDefault: 'Client ID', sensitive: false },
  client_secret: { key: 'sso.clientSecret', labelDefault: 'Client secret', sensitive: true },
  admin_email: {
    key: 'sso.adminEmail',
    labelDefault: 'Admin email',
    sensitive: false,
    placeholder: 'super-admin@yourdomain.com',
  },
  client_email: {
    key: 'sso.clientEmail',
    labelDefault: 'Service account client email',
    sensitive: false,
  },
  private_key: { key: 'sso.privateKey', labelDefault: 'Private key', sensitive: true },
};

function parseValues(text: string): Record<string, string> {
  if (!text.trim()) return {};
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        out[k] = typeof v === 'string' ? v : String(v ?? '');
      }
      return out;
    }
  } catch {
    // Parent's raw text is the source of truth; an unparseable value
    // here just means we render empty inputs.
  }
  return {};
}

export function CredentialInput({
  hasCredentials,
  credentialShape,
  rawIdpCredentialsText,
  onIdpCredentialsTextChange,
}: CredentialInputProps) {
  const { t } = useTranslation();
  const keys = credentialShape?.required_keys ?? [];

  const [values, setValues] = useState<Record<string, string>>(() =>
    parseValues(rawIdpCredentialsText),
  );

  // Re-sync from the parent when the underlying raw text resets (e.g.,
  // drawer hydrate). Skip the typed-by-user case (raw text already
  // mirrors our last emit) to avoid clobbering local state mid-edit.
  useEffect(() => {
    const next = parseValues(rawIdpCredentialsText);
    setValues((prev) => {
      const sameKeys =
        Object.keys(prev).length === Object.keys(next).length &&
        Object.keys(prev).every((k) => prev[k] === next[k]);
      return sameKeys ? prev : next;
    });
  }, [rawIdpCredentialsText]);

  const anyFilled = useMemo(
    () => keys.some((k) => (values[k] ?? '').length > 0),
    [keys, values],
  );

  const handleChange = (key: string, value: string) => {
    const next = { ...values, [key]: value };
    setValues(next);
    // Only emit non-empty entries; an empty object emits "" which the
    // backend treats as "no change" on update.
    const filtered = Object.fromEntries(
      Object.entries(next).filter(([, v]) => v.length > 0),
    );
    onIdpCredentialsTextChange(
      Object.keys(filtered).length ? JSON.stringify(filtered) : '',
    );
  };

  if (keys.length === 0) {
    return null;
  }

  return (
    <VStack align="stretch" gap={3}>
      {keys.map((key) => {
        const meta = FIELD_META[key] ?? { key: `sso.${key}`, labelDefault: key, sensitive: false };
        const labelText = t(meta.key, meta.labelDefault);
        const placeholder = hasCredentials && !anyFilled
          ? '••••••••••'
          : meta.placeholder;
        return (
          <Field.Root key={key}>
            <Field.Label htmlFor={`sso-cred-${key}`}>
              {labelText}
            </Field.Label>
            <InputField
              id={`sso-cred-${key}`}
              data-e2e={`sso-cred-${key}`}
              type={meta.sensitive ? 'password' : 'text'}
              value={values[key] ?? ''}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder={placeholder}
              autoComplete="off"
            />
          </Field.Root>
        );
      })}
    </VStack>
  );
}
